import React, { useState, useMemo } from 'react';
import { X, Minus, Plus } from 'lucide-react';
import FoodImage from './FoodImage';

/**
 * Item detail modal — flat add-ons UX (no group names, no required, no
 * "Choose N" — every add-on is independently checkable with a per-addon
 * quantity stepper up to `max_quantity`).
 *
 * Backward compatible with items saved under the legacy grouped schema:
 * we flatten `item.modifier_groups[].options[]` into one list. The cart
 * still receives selected modifiers in the same shape as before
 * `{ group, option, price_delta, quantity }`.
 */
const ItemDetailModal = ({ item, onClose, onAddToCart }) => {
  // Key the inner component on `item?.id` so React fully resets state
  // when a different item is opened — no `useEffect` → `setState`
  // antipattern needed.
  if (!item) return null;
  return <ItemDetailInner key={item.id || 'empty'} item={item} onClose={onClose} onAddToCart={onAddToCart} />;
};

const ItemDetailInner = ({ item, onClose, onAddToCart }) => {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  // selections keyed by addon index → quantity (0 = not selected)
  const [picked, setPicked] = useState({});

  // Flatten legacy groups → single add-on list.
  const addons = useMemo(() => {
    const flat = [];
    (item?.modifier_groups || []).forEach((g) => {
      (g.options || []).forEach((opt) => {
        flat.push({
          group: g.name || 'Add-ons',
          name: opt.name,
          price_delta: Number(opt.price_delta || 0),
          max_quantity: Math.max(1, parseInt(opt.max_quantity || 1, 10) || 1),
        });
      });
    });
    return flat;
  }, [item]);

  if (!item) return null;

  const stepAddon = (idx, delta) => {
    setPicked((cur) => {
      const max = addons[idx]?.max_quantity ?? 1;
      const next = Math.max(0, Math.min(max, (cur[idx] || 0) + delta));
      const out = { ...cur };
      if (next === 0) delete out[idx];
      else out[idx] = next;
      return out;
    });
  };

  // Build the selected modifiers list for the cart.
  const selectedModifiers = addons
    .map((a, idx) => {
      const qty = picked[idx] || 0;
      if (qty < 1) return null;
      return {
        group: a.group,
        option: a.name,
        price_delta: a.price_delta,
        quantity: qty,
      };
    })
    .filter(Boolean);

  const addonsTotal = selectedModifiers.reduce(
    (s, m) => s + m.price_delta * m.quantity,
    0,
  );
  const lineTotal = (Number(item.price) + addonsTotal) * quantity;

  const handleAdd = () => {
    onAddToCart({
      ...item,
      quantity,
      notes,
      modifiers: selectedModifiers,
      // Effective unit price stored separately so cart math stays correct
      effective_price: Number(item.price) + addonsTotal,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="item-detail-modal"
      >
        {/* Sticky compact header — always visible while scrolling so the
            customer never loses sight of WHICH item they're customising. */}
        <div
          className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-slate-100 px-4 sm:px-5 py-3 flex items-center gap-3"
          data-testid="item-detail-sticky-header"
        >
          {item.image_url && (
            <FoodImage
              src={item.image_url}
              alt=""
              variant="thumb"
              aspectClass="w-9 h-9 flex-shrink-0"
              rounded="rounded-lg"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-bold text-slate-900 truncate" data-testid="item-detail-sticky-name">
              {item.name}
            </div>
            <div className="text-[11.5px] text-gold-600 font-semibold">
              ${Number(item.price).toFixed(2)}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors flex-shrink-0"
            aria-label="Close"
            data-testid="item-detail-sticky-close"
          >
            <X className="w-4.5 h-4.5 text-slate-700" />
          </button>
        </div>

        {/* Photo — compact banner (40-50% shorter than before) so the
            extras section sits above the fold on mobile. FoodImage keeps
            lazy-load + srcSet, but we constrain height directly rather
            than using `aspect-video` which dominated the viewport. */}
        {item.image_url && (
          <FoodImage
            src={item.image_url}
            alt={item.name}
            variant="detail"
            className="h-36 sm:h-44"
            testid="item-detail-photo"
          />
        )}

        <div className="px-5 pt-4 pb-5">
          {/* Compact title row — name + price inline so the description
              + extras section move up the viewport. */}
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight">
              {item.name}
            </h2>
            <p className="text-gold-600 font-bold whitespace-nowrap text-base sm:text-lg">
              ${Number(item.price).toFixed(2)}
            </p>
          </div>
          {item.description && (
            <p className="text-[13px] text-slate-500 mt-1.5 leading-snug line-clamp-2">
              {item.description}
            </p>
          )}

          {/* "Customize available" hint — appears IMMEDIATELY under the
              title when add-ons exist, so the customer cannot miss them
              even on a 375px viewport. */}
          {addons.length > 0 && (
            <div
              className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gold-50 border border-gold-300 text-gold-700 text-[11px] font-black uppercase tracking-[0.14em]"
              data-testid="item-detail-extras-hint"
            >
              <Plus className="w-3 h-3" />
              {`${addons.length} ${addons.length === 1 ? 'extra' : 'extras'} available`}
            </div>
          )}

          {/* Add-ons (flat list, all optional) — promoted to first
              section under the title for maximum visibility. */}
          {addons.length > 0 && (
            <div className="mt-4" data-testid="item-addons">
              <div className="flex items-baseline justify-between mb-1">
                <h3 className="font-bold text-slate-900 text-[15px]">Customize your item</h3>
                <span className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold">
                  Optional
                </span>
              </div>
              <p className="text-xs text-slate-500 mb-3">Add as many as you&apos;d like.</p>
              <div className="space-y-2">
                {addons.map((a, idx) => {
                  const qty = picked[idx] || 0;
                  const selected = qty > 0;
                  const supportsQty = a.max_quantity > 1;
                  return (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all ${
                        selected
                          ? 'border-gold-500 bg-gold-50'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`addon-${idx}`}
                    >
                      {/* Checkbox / indicator */}
                      <button
                        type="button"
                        onClick={() => stepAddon(idx, selected ? -qty : 1)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          selected ? 'border-gold-500 bg-gold-500' : 'border-slate-300 bg-white'
                        }`}
                        data-testid={`addon-${idx}-toggle`}
                        aria-label={selected ? 'Remove' : 'Add'}
                      >
                        {selected && (
                          <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path
                              fillRule="evenodd"
                              d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 011.4-1.4L9 11.6l6.3-6.3a1 1 0 011.4 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </button>

                      {/* Name + price */}
                      <button
                        type="button"
                        onClick={() => stepAddon(idx, selected ? -qty : 1)}
                        className="flex-1 text-left min-w-0"
                      >
                        <div className="text-sm font-medium text-slate-800 truncate">{a.name}</div>
                        {a.price_delta > 0 && (
                          <div className="text-[12px] text-slate-500">+${a.price_delta.toFixed(2)} each</div>
                        )}
                      </button>

                      {/* Quantity stepper (only when max_quantity > 1 and selected) */}
                      {supportsQty && selected ? (
                        <div className="flex items-center gap-1 border border-gold-300 rounded-full bg-white px-1">
                          <button
                            type="button"
                            onClick={() => stepAddon(idx, -1)}
                            className="w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center"
                            aria-label="Decrease"
                            data-testid={`addon-${idx}-minus`}
                          >
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span
                            className="w-5 text-center text-sm font-bold text-slate-900 tabular-nums"
                            data-testid={`addon-${idx}-qty`}
                          >
                            {qty}
                          </span>
                          <button
                            type="button"
                            onClick={() => stepAddon(idx, 1)}
                            disabled={qty >= a.max_quantity}
                            className="w-7 h-7 rounded-full hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                            aria-label="Increase"
                            data-testid={`addon-${idx}-plus`}
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : null}

                      {/* Per-line total when selected (and max_qty > 1) */}
                      {selected && a.price_delta > 0 && (
                        <span
                          className="text-[12px] font-semibold text-slate-700 tabular-nums w-14 text-right"
                          data-testid={`addon-${idx}-subtotal`}
                        >
                          ${(a.price_delta * qty).toFixed(2)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="flex items-center justify-between mt-5 border-t border-slate-100 pt-4">
            <span className="font-medium text-slate-800">Quantity</span>
            <div className="flex items-center gap-3 border border-slate-200 rounded-full px-2 py-1">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-7 h-7 rounded-full hover:bg-slate-100"
                data-testid="qty-minus"
              >
                <Minus className="w-4 h-4 mx-auto" />
              </button>
              <span className="font-semibold w-5 text-center" data-testid="qty-value">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="w-7 h-7 rounded-full hover:bg-slate-100"
                data-testid="qty-plus"
              >
                <Plus className="w-4 h-4 mx-auto" />
              </button>
            </div>
          </div>

          {/* Notes for the kitchen — allergies, gluten-free, dietary
              restrictions, special preparation instructions, etc. The
              restaurant sees this text on the order ticket. Free text,
              no price/time impact. */}
          <div className="mt-4 border-t border-slate-100 pt-4">
            <label className="block text-sm font-bold text-slate-900 mb-2">
              Notes for the kitchen{' '}
              <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="e.g. no onions, extra napkins, allergy info, gluten free…"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 focus:border-gold-500 focus:ring-2 focus:ring-gold-500/20 outline-none text-sm resize-none"
              data-testid="item-notes"
            />
          </div>
        </div>

        {/* Sticky CTA — real-time price */}
        <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4">
          <button
            onClick={handleAdd}
            className="w-full py-4 rounded-xl bg-gold-500 hover:bg-gold-400 text-black font-bold text-base transition-colors flex items-center justify-between px-5"
            data-testid="add-to-cart-btn"
          >
            <span>Add {quantity} to cart</span>
            <span data-testid="add-to-cart-total">${lineTotal.toFixed(2)}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemDetailModal;
